import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import ButtonLink from '../../components/atoms/button-link/button-link';
import LoadingIcon from '../../components/atoms/loading-icon/loading-icon';
import TabTitle from '../../components/atoms/tab-title/tab-title';
import { MetaPages } from '../../enums/pages';
import SocialMediaShareSection from '../../components/molecules/social-media-share-section/social-media-share-section';
import { getFeaturedFriendsOfTheKitchenPageDetails } from '../../services/inspirations-and-planning';
import { FriendsOfTheKitchenPageDetailsType } from '../../types/content';
import Breadcrumbs from '../../components/atoms/breadcrumbs/breadcrumbs';
import ContainerWithSidebar from '../../components/atoms/container-with-sidebar/container-with-sidebar';
import MarginSpacing from '../../components/atoms/margin-spacing/margin-spacing';
import FeaturesSidebar from '../../components/molecules/features-sidebar/features-sidebar';
import { pageRoute } from '../../config/routes/pages';
import { NewsAndEventsModel } from '../../interface/news-and-events';
import {
  getAllNewsAndEventsContent,
  getFeaturedNewsAndEvents,
  getNewsAndEventsContentById
} from '../../services/news';

const Container = styled.div`
  p {
    font-family: MuseoSans-100;
    font-size: 14px;
    font-weight: 200 !important;
    margin-bottom: 15px;
  }

  em {
    font-size: 12px;
  }

  p strong {
    font-family: MuseoSans-500;
  }

  ul {
    list-style-type: circle;
    padding-left: 40px;
    font-family: MuseoSans-100;
    font-size: 14px;
  }

  ol {
    list-style-type: number;
    font-family: MuseoSans-100;
    font-size: 14px;
  }

  h1 {
    font-family: ChronicleDisplayLight;
    font-size: 35px;
    font-weight: 300 !important;
    text-transform: uppercase;
    line-height: 2.25rem;
  }

  a {
    text-decoration-line: underline;
  }

  a:hover {
    color: #0081c6;
  }
`;

type IndexProps = {
  featuredFriendsOfTheKitchenPageDetails: FriendsOfTheKitchenPageDetailsType[];
  featuredNewsAndEvents: NewsAndEventsModel[];
  newsAndEventsArticle: NewsAndEventsModel;
};

export function Index(props: IndexProps) {
  const {
    featuredFriendsOfTheKitchenPageDetails,
    featuredNewsAndEvents,
    newsAndEventsArticle
  } = props;

  const router = useRouter();

  if (router.isFallback) {
    return <LoadingIcon />;
  }

  const { initial_rich_text, final_rich_text, youtube_link } =
    newsAndEventsArticle.attributes;

  const breadcrumbs = {
    route: newsAndEventsArticle.attributes.slug,
    tabLabel: newsAndEventsArticle.attributes.news_title
  };

  const newsAndEvents = {
    route: pageRoute.NEWS_AND_EVENTS,
    tabLabel: 'NEWS AND EVENTS'
  };

  const youtubeVideo = youtube_link ? (
    <iframe
      allowFullScreen
      className="w-full md:w-3/4 h-96 py-4 my-6 lg:p-4 m-auto"
      src={youtube_link}
    />
  ) : (
    ''
  );

  return (
    <>
      <TabTitle page={MetaPages.NEWS_AND_EVENTS} />
      <ContainerWithSidebar
        featuredContents={
          <FeaturesSidebar
            friendsOfTheKitchenPageDetails={
              featuredFriendsOfTheKitchenPageDetails
            }
            newsAndEventsContent={featuredNewsAndEvents}
          />
        }
      >
        <Breadcrumbs
          color="text-breadcrumbGray"
          subTab={breadcrumbs}
          tab={newsAndEvents}
        />
        <Container>
          <div className="mt-0 sm:mt-10" />
          <ReactMarkdown>{initial_rich_text}</ReactMarkdown>
          {youtubeVideo}
          <ReactMarkdown>{final_rich_text}</ReactMarkdown>
          <div className="flex justify-end ml-auto mt-10">
            <ButtonLink
              buttonTitle="BACK"
              ctaLink={pageRoute.NEWS_AND_EVENTS}
            />
          </div>
          <MarginSpacing margin="mb-20" />
        </Container>
      </ContainerWithSidebar>
      <MarginSpacing margin="mb-5" />
      <SocialMediaShareSection />
    </>
  );
}

export async function getStaticProps({ params }: any) {
  const featuredFriendsOfTheKitchenPageDetails =
    await getFeaturedFriendsOfTheKitchenPageDetails();
  const featuredNewsAndEvents = await getFeaturedNewsAndEvents();
  const newsAndEventsArticle = await getNewsAndEventsContentById(params.id);

  return {
    props: {
      featuredFriendsOfTheKitchenPageDetails,
      featuredNewsAndEvents,
      newsAndEventsArticle
    },
    revalidate: 300
  };
}

export async function getStaticPaths() {
  const newsAndEventsContent = await getAllNewsAndEventsContent();

  const paths = newsAndEventsContent.map((newsItem) => {
    return {
      params: { id: newsItem.attributes.slug }
    };
  });

  return {
    paths: paths,
    fallback: true
  };
}

export default Index;
